import React, { useState, useEffect, useRef } from 'react'
import { Bell, Check, CheckCheck, Info, AlertTriangle, CheckCircle, XCircle, X, DollarSign, TrendingUp, TrendingDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import notificacoesService, { Notificacao } from '../../services/notificacoes'
import orcamentoNotificationsService, { OrcamentoNotification } from '../../services/orcamentoNotifications'
import Button from '../ui/Button'

const NotificacoesDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'geral' | 'aprovados' | 'rejeitados'>('geral')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Query para buscar notificações gerais
  const { data: notificacoes = [], isLoading } = useQuery({
    queryKey: ['notificacoes'],
    queryFn: () => notificacoesService.listar(),
    refetchInterval: 60000 // Atualizar a cada minuto
  })

  // Query para contar não lidas gerais
  const { data: naoLidasCount = 0 } = useQuery({
    queryKey: ['notificacoes', 'nao-lidas'],
    queryFn: () => notificacoesService.contarNaoLidas(),
    refetchInterval: 60000
  })

  // Query para buscar notificações de orçamentos aprovados
  const { data: orcamentosAprovados = [] } = useQuery({
    queryKey: ['orcamento-notifications', 'aprovados'],
    queryFn: () => orcamentoNotificationsService.listarAprovados(),
    refetchInterval: 60000
  })

  // Query para buscar notificações de orçamentos rejeitados
  const { data: orcamentosRejeitados = [] } = useQuery({
    queryKey: ['orcamento-notifications', 'rejeitados'],
    queryFn: () => orcamentoNotificationsService.listarRejeitados(),
    refetchInterval: 60000
  })

  // Query para contar orçamentos aprovados não lidos
  const { data: aprovadosNaoLidos = 0 } = useQuery({
    queryKey: ['orcamento-notifications', 'aprovados', 'count'],
    queryFn: () => orcamentoNotificationsService.contarNaoLidasAprovados(),
    refetchInterval: 60000
  })

  // Query para contar orçamentos rejeitados não lidos
  const { data: rejeitadosNaoLidos = 0 } = useQuery({
    queryKey: ['orcamento-notifications', 'rejeitados', 'count'],
    queryFn: () => orcamentoNotificationsService.contarNaoLidasRejeitados(),
    refetchInterval: 60000
  })

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificacaoClick = async (notificacao: Notificacao) => {
    // Marcar como lida
    if (!notificacao.lida) {
      await notificacoesService.marcarComoLida(notificacao.id)
      queryClient.invalidateQueries({ queryKey: ['notificacoes'] })
      queryClient.invalidateQueries({ queryKey: ['notificacoes', 'nao-lidas'] })
    }

    // Navegar para o link se existir
    if (notificacao.link) {
      navigate(notificacao.link)
      setIsOpen(false)
    }
  }

  const handleOrcamentoNotificacaoClick = async (notificacao: OrcamentoNotification) => {
    // Marcar como lida
    if (!notificacao.lida) {
      await orcamentoNotificationsService.marcarComoLida(notificacao.id)
      queryClient.invalidateQueries({ queryKey: ['orcamento-notifications'] })
    }

    // Navegar para o link se existir
    if (notificacao.link) {
      navigate(notificacao.link)
      setIsOpen(false)
    }
  }

  const handleMarcarTodasLidas = async () => {
    await notificacoesService.marcarTodasComoLidas()
    queryClient.invalidateQueries({ queryKey: ['notificacoes'] })
    queryClient.invalidateQueries({ queryKey: ['notificacoes', 'nao-lidas'] })
  }

  const getIconByTipo = (tipo: string) => {
    switch (tipo) {
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />
      case 'sucesso':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'aviso':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      case 'erro':
        return <XCircle className="h-4 w-4 text-red-500" />
      default:
        return <Info className="h-4 w-4 text-gray-500" />
    }
  }

  const formatarData = (dataStr: string) => {
    const data = new Date(dataStr)
    const agora = new Date()
    const diffMs = agora.getTime() - data.getTime()
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffHoras < 1) {
      const diffMinutos = Math.floor(diffMs / (1000 * 60))
      return `Há ${diffMinutos} minuto${diffMinutos !== 1 ? 's' : ''}`
    } else if (diffHoras < 24) {
      return `Há ${diffHoras} hora${diffHoras !== 1 ? 's' : ''}`
    } else if (diffDias < 7) {
      return `Há ${diffDias} dia${diffDias !== 1 ? 's' : ''}`
    } else {
      return data.toLocaleDateString('pt-PT')
    }
  }

  // Calcular total de notificações não lidas
  const totalNaoLidas = naoLidasCount + aprovadosNaoLidos + rejeitadosNaoLidos

  // Obter lista de notificações baseada na aba ativa
  const getNotificacoesAtivas = () => {
    switch (activeTab) {
      case 'aprovados':
        return orcamentosAprovados
      case 'rejeitados':
        return orcamentosRejeitados
      default:
        return notificacoes
    }
  }

  const notificacoesAtivas = getNotificacoesAtivas()

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="h-5 w-5" />
        {totalNaoLidas > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">
            {totalNaoLidas > 9 ? '9+' : totalNaoLidas}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Notificações</h3>
              <div className="flex items-center space-x-2">
                {totalNaoLidas > 0 && (
                  <button
                    onClick={handleMarcarTodasLidas}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                  >
                    <CheckCheck className="h-3 w-3" />
                    <span>Marcar todas como lidas</span>
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('geral')}
                className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'geral'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Bell className="h-3 w-3" />
                <span>Geral</span>
                {naoLidasCount > 0 && (
                  <span className="bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                    {naoLidasCount > 9 ? '9+' : naoLidasCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('aprovados')}
                className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'aprovados'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <TrendingUp className="h-3 w-3" />
                <span>Aprovados</span>
                {aprovadosNaoLidos > 0 && (
                  <span className="bg-green-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                    {aprovadosNaoLidos > 9 ? '9+' : aprovadosNaoLidos}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('rejeitados')}
                className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'rejeitados'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <TrendingDown className="h-3 w-3" />
                <span>Rejeitados</span>
                {rejeitadosNaoLidos > 0 && (
                  <span className="bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                    {rejeitadosNaoLidos > 9 ? '9+' : rejeitadosNaoLidos}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Lista de notificações */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : notificacoesAtivas.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Nenhuma notificação</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notificacoesAtivas.map((notificacao) => (
                  <div
                    key={notificacao.id}
                    onClick={() => {
                      if (activeTab === 'geral') {
                        handleNotificacaoClick(notificacao as Notificacao)
                      } else {
                        handleOrcamentoNotificacaoClick(notificacao as OrcamentoNotification)
                      }
                    }}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notificacao.lida ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getIconByTipo(notificacao.tipo)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={`text-sm font-medium text-gray-900 ${
                              !notificacao.lida ? 'font-semibold' : ''
                            }`}>
                              {notificacao.titulo}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {notificacao.mensagem}
                            </p>
                            {activeTab !== 'geral' && (notificacao as OrcamentoNotification).valorTotal && (
                              <p className="text-sm font-medium text-green-600 mt-1">
                                €{(notificacao as OrcamentoNotification).valorTotal?.toFixed(2)}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {formatarData(notificacao.data)}
                            </p>
                          </div>
                          {!notificacao.lida && (
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${
                              activeTab === 'aprovados' ? 'bg-green-500' :
                              activeTab === 'rejeitados' ? 'bg-red-500' : 'bg-blue-500'
                            }`}></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notificacoesAtivas.length > 0 && (
            <div className="p-3 border-t border-gray-200">
              <button
                onClick={() => {
                  if (activeTab === 'geral') {
                    navigate('/notificacoes')
                  } else {
                    navigate('/orcamentos')
                  }
                  setIsOpen(false)
                }}
                className="text-sm text-blue-600 hover:text-blue-700 w-full text-center"
              >
                {activeTab === 'geral'
                  ? 'Ver todas as notificações'
                  : `Ver todos os orçamentos ${activeTab}`
                }
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificacoesDropdown